import { Box, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { adminApiService } from "../../../utils/api";
import { FlyerScraperSource, MuiFilterRequestDto } from "@swagger/typescript-fetch-goflyer";

import { GridFilterModel, GridPaginationModel, GridRowSelectionModel } from "@mui/x-data-grid";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { useTranslation } from "react-i18next";

import { messages } from "../../../locales/messages";
import { GridSortModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { TableData } from "../../../utils/interface/tableData";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { useFlyerScraperTableColumns } from "./useFlyerScraperTableColumns";
import {
  SetupPageContainer,
  SetupPageTopAppBar,
} from "../../../utils/commonStyledComponents/commonStyled";
import { BlackFontTransparentButton } from "../../../utils/commonStyledComponents/commonMuiStyled";
import { LeftButtonSection, TableWrapper } from "./styled";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { routes } from "../../../utils/constant/navigationRoutes";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";

const FlyerScraperPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authHeaders = useAuthHeaders();

  const [paginationLoading, setPaginationLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [flyerScraperListData, setFlyerScraperListData] =
    useState<TableData<FlyerScraperSource>>();
  const [open, setOpen] = useState(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const { paginationModel, sortModel, handleTableChange } = useQueryParams();

  const columns = useFlyerScraperTableColumns();
  /**
   * Opens a confirmation modal to delete a Upload Store.
   *
   * @param value - The Upload Store data to be deleted.
   */
  const handleOpenDelete = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const fetchFlyerScraperListWithFilter = async (
    activeFilterModel: GridFilterModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.flyerScraperSourceAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setFlyerScraperListData((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
    );
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const fetchFlyerScraperList = async (
    paginationModel: GridPaginationModel,
    gridSortModel: GridSortModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () =>
        adminApiService.getManyBaseFlyerScraperSourceAdminControllerFlyerScraperSource(
          undefined,
          undefined,
          undefined,
          undefined,
          gridSortModel?.length
            ? [
                `${
                  gridSortModel[0].field
                },${gridSortModel[0]?.sort?.toUpperCase()}`,
              ]
            : [`updatedAt,DESC`],
          undefined,
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
          undefined,
        ),
      (flyerScraperList) => {
        setFlyerScraperListData((prev) => ({
          ...prev,
          rows: flyerScraperList.data,
          total: flyerScraperList?.total,
        }));
      },
    );
  };

  const handleDeleteStore = async () => {
    if (rowSelectionModel?.length) {
      try {
        setPaginationLoading(true);
        const result = rowSelectionModel?.map(async (value) => {
          return await adminApiService.deleteOneBaseFlyerScraperSourceAdminControllerFlyerScraperSource(
            value as string,
            authHeaders,
          );
        });
        await Promise.all(result);

        if (muiFilterModelHasActiveItems(filterModel)) {
          fetchFlyerScraperListWithFilter(filterModel);
        } else {
          fetchFlyerScraperList(paginationModel, sortModel);
        }

        setPaginationLoading(false);
        handleCancel();
        toastSuccess(t(messages.Successfully_Deleted()));
      } catch (error) {
        console.log("error", error);
        setPaginationLoading(false);
        toastError(
          t(messages.There_is_an_error_please_Contact_Administrator()),
        );
      }
    }
  };

  useEffect(() => {
    setFlyerScraperListData({
      columns: columns,
    });
  }, []);

  useEffect(() => {
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (hasActiveFilters) {
      fetchFlyerScraperListWithFilter(filterModel);
    } else {
      fetchFlyerScraperList(paginationModel, sortModel);
    }
  }, [paginationModel, sortModel, filterModel]);

  return (
    <SetupPageContainer>
      <AccountCardWrapper title={"Flyer Scraper Source"}>
        <Box>
          <SetupPageTopAppBar>
            <LeftButtonSection>
              <Stack direction="row" spacing={2}>
                <PermissionComponentWrapper
                  requiredPermission={["create"]}
                  requiredPermissionGroup={["flyerScraper"]}
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <UploadFileIcon
                        style={{ color: "#ff8038", fontSize: 25 }}
                      />
                    }
                    onClick={() => {
                      navigate(routes.flyerScraper.create);
                    }}
                  >
                    {t(messages.Create())}
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>
                <PermissionComponentWrapper
                  requiredPermission={["delete"]}
                  requiredPermissionGroup={["flyerScraper"]}
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <DeleteIcon
                        style={{
                          color: "#ff8038",
                          fontSize: 25,
                          opacity: !rowSelectionModel?.length ? 0.5 : 1,
                        }}
                      />
                    }
                    disabled={!rowSelectionModel?.length}
                    onClick={handleOpenDelete}
                  >
                    {t(messages.Delete())}
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>
              </Stack>
            </LeftButtonSection>
          </SetupPageTopAppBar>
          <TableWrapper>
            <ListTableQueryPagination
              loadFailed={listRequestGuard.loadFailed}
              columns={flyerScraperListData?.columns}
              rows={flyerScraperListData?.rows}
              total={paginationLoading ? 0 : flyerScraperListData?.total}
              paginationModel={paginationModel}
              sortModel={sortModel}
              isLoading={paginationLoading}
              handleRowSelection={(value) => {
                setRowSelectionModel(value);
              }}
              onTableChange={handleTableChange}
              dynamicHeightAdjuster={230}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
            />
          </TableWrapper>

          <MuiConfirmationModal
            open={open}
            handleCancel={handleCancel}
            handleConfirm={handleDeleteStore}
            title={`${t(messages.Delete_Confirmation())}`}
            description={`
        ${t(messages.Are_you_sure_you_want_to_delete_These())} ${t(
          messages.Selected_Items(),
          { count: rowSelectionModel.length },
        )}?  ${t(messages.This_action_cannot_be_undone())}.
      `}
          />
        </Box>
      </AccountCardWrapper>
    </SetupPageContainer>
  );
};

export default FlyerScraperPage;
